import { watch } from 'vue'
import { useUserStore } from '@/stores/UserStore'
import { apiClient } from '@/helpers/api.js'
import { handleApiError } from '@/services/errorHandler'

const CANNY_APP_ID = import.meta.env.VITE_CANNY_APP_ID

// The board widget runs in an iframe on canny.io, so `identify` below can't log
// anyone into it — `ssoToken` is its ONLY identity input. It's our own user
// object (id / email / name), signed server-side with the Canny private key, so
// Canny trusts it: no signup screen, no Canny credentials, nothing the user sees.
// Without it the widget treats every visitor as anonymous and prompts them to
// create a Canny account before they can post.
let ssoToken = null

/**
 * The signed identity token for the feedback board, from
 * `GET /api/v2/canny/sso-token`. Cached for the page load — it's the same user
 * the whole time — and resolves to `null` on failure (nothing cached, so the
 * next open retries) so the dialog still opens anonymously rather than breaking.
 */
export const fetchCannySsoToken = async () => {
  if (ssoToken) return ssoToken

  try {
    const { data } = await apiClient().get('/canny/sso-token')

    ssoToken = data?.token || data?.ssoToken || (typeof data === 'string' ? data : null)

    return ssoToken
  } catch (error) {
    handleApiError(error, { component: 'useCanny', action: 'fetchCannySsoToken' })
    return null
  }
}

const loadSDK = () => {
  if (document.getElementById('canny-jssdk')) return
  ;(function (w, d, i, s) {
    function l() {
      if (!d.getElementById(i)) {
        const f = d.getElementsByTagName(s)[0]
        const e = d.createElement(s)
        e.type = 'text/javascript'
        e.async = true
        e.src = 'https://sdk.canny.io/sdk.js'
        e.id = i
        f.parentNode.insertBefore(e, f)
      }
    }
    if (typeof w.Canny !== 'function') {
      const c = function () { c.q.push(arguments) }
      c.q = []
      w.Canny = c
      if (d.readyState === 'complete') {
        l()
      } else if (w.attachEvent) {
        w.attachEvent('onload', l)
      } else {
        w.addEventListener('load', l, false)
      }
    }
  })(window, document, 'canny-jssdk', 'script')
}

const identify = (user) => {
  if (!CANNY_APP_ID || !user?.id || !user?.email) return
  try {
    window.Canny('identify', {
      appID: CANNY_APP_ID,
      user: {
        id: String(user.id),
        email: user.email,
        name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email,
        created: user.created_at ? new Date(user.created_at).toISOString() : undefined,
      },
    })
  } catch (e) {
    console.error('Canny identify failed:', e)
  }
}

export const useCanny = () => {
  const userStore = useUserStore()

  loadSDK()

  watch(() => userStore.user, (user) => {
    if (user) identify(user)
  }, { immediate: true })
}
